// Location: src/features/devices/config/deviceCategories.ts

import type { DeviceType, Channel } from '@/types/models';

/**
 * Device category enum - User-friendly categories shown in UI
 */
export enum DeviceCategory {
  KioskOnPremise = 'kiosk_onpremise',
  KioskOnline = 'kiosk_online',
  GuestMobile = 'guest_mobile',
  OnlineCheckin = 'online_checkin',
  MobileDesk = 'mobile_desk',
  Dispenser = 'dispenser'
}

/**
 * Category configuration interface
 */
export interface DeviceCategoryConfig {
  category: DeviceCategory;
  labelKey: string;
  descriptionKey: string;
  deviceType: DeviceType;
  channel: Channel;
  icon?: string;
}

/**
 * Complete category configuration
 * Maps UI categories to backend deviceType + channel
 */
export const DEVICE_CATEGORIES: DeviceCategoryConfig[] = [
  {
    category: DeviceCategory.KioskOnPremise,
    labelKey: 'resources.devices.categories.kiosk_onpremise',
    descriptionKey: 'resources.devices.categoryDescriptions.kiosk_onpremise',
    deviceType: 'Kiosk',
    channel: 'Kiosk',
    icon: 'computer'
  },
  {
    category: DeviceCategory.KioskOnline,
    labelKey: 'resources.devices.categories.kiosk_online',
    descriptionKey: 'resources.devices.categoryDescriptions.kiosk_online',
    deviceType: 'Kiosk',
    channel: 'MobileKiosk',
    icon: 'tablet'
  },
  {
    category: DeviceCategory.GuestMobile,
    labelKey: 'resources.devices.categories.guest_mobile',
    descriptionKey: 'resources.devices.categoryDescriptions.guest_mobile',
    deviceType: 'Mobile',
    channel: 'MobileKiosk',
    icon: 'smartphone'
  },
  {
    category: DeviceCategory.OnlineCheckin,
    labelKey: 'resources.devices.categories.online_checkin',
    descriptionKey: 'resources.devices.categoryDescriptions.online_checkin',
    deviceType: 'Virtual',
    channel: 'Online',
    icon: 'web'
  },
  {
    category: DeviceCategory.MobileDesk,
    labelKey: 'resources.devices.categories.mobile_desk',
    descriptionKey: 'resources.devices.categoryDescriptions.mobile_desk',
    deviceType: 'Virtual',
    channel: 'AirLobby',
    icon: 'desk'
  },
  {
    category: DeviceCategory.Dispenser,
    labelKey: 'resources.devices.categories.dispenser',
    descriptionKey: 'resources.devices.categoryDescriptions.dispenser',
    deviceType: 'Dispenser',
    channel: 'Dispenser',
    icon: 'credit_card'
  }
];

/**
 * Convert UI category to backend fields (deviceType + channel)
 */
export function getCategoryBackendFields(category: DeviceCategory): {
  deviceType: DeviceType;
  channel: Channel;
} {
  const config = DEVICE_CATEGORIES.find(c => c.category === category);
  
  if (!config) {
    throw new Error(`Unknown device category: ${category}`);
  }
  
  return {
    deviceType: config.deviceType,
    channel: config.channel
  };
}

/**
 * Convert backend fields (deviceType + channel) to UI category
 * Returns null if combination is not found
 */
export function getBackendFieldsCategory(
  deviceType: DeviceType,
  channel: Channel
): DeviceCategory | null {
  const config = DEVICE_CATEGORIES.find(
    c => c.deviceType === deviceType && c.channel === channel
  );
  
  return config ? config.category : null;
}

/**
 * Get category configuration by category value
 */
export function getCategoryConfig(category: DeviceCategory): DeviceCategoryConfig | undefined {
  return DEVICE_CATEGORIES.find(c => c.category === category);
}

/**
 * Get choices for SelectInput in React Admin format
 * Returns array with { id, name } for use in SelectInput
 */
export function getCategoryChoices(translate: (key: string) => string): Array<{ id: string; name: string }> {
  return DEVICE_CATEGORIES.map(cat => ({
    id: cat.category,
    name: translate(cat.labelKey)
  }));
}

/**
 * Validate if a category value is valid
 */
export function isValidCategory(category: string): category is DeviceCategory {
  return Object.values(DeviceCategory).includes(category as DeviceCategory);
}

/**
 * Transform device data from backend for editing
 * Adds category field based on deviceType + channel
 */
export function transformDeviceForEdit(device: any) {
  const category = getBackendFieldsCategory(device.deviceType, device.channel);
  
  return {
    ...device,
    category: category || DeviceCategory.KioskOnPremise // fallback to default
  };
}

/**
 * Transform form data for backend API
 * Removes category and adds deviceType + channel
 */
export function transformDeviceForCreate(formData: any) {
  const { category, ...rest } = formData;
  const { deviceType, channel } = getCategoryBackendFields(category);
  
  return {
    ...rest,
    deviceType,
    channel
  };
}

/**
 * Transform form data for backend API update
 * Only transforms if category is present
 */
export function transformDeviceForUpdate(formData: any) {
  if (!formData.category) {
    return formData;
  }
  
  const { category, ...rest } = formData;
  const { deviceType, channel } = getCategoryBackendFields(category);
  
  return {
    ...rest,
    deviceType,
    channel
  };
}